import { useReactiveVar } from "@apollo/client";
import { compact } from "lodash";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  leftSidePanelVar,
  mobileSidebarOpenVar,
} from "@cache/cache";
import Link from "@components/link/link";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { role } from "@helpers/constants";
import { classNames } from "@helpers/css";

const AiIcon = getTopicflowIcon("ai");
const UserIcon = getTopicflowIcon("user");
const MeetingIcon = getTopicflowIcon("meeting");
const NotificationIcon = getTopicflowIcon("today");
const ExportIcon = getTopicflowIcon("exit");
const AdjustmentsIcon = getTopicflowIcon("adjustments");
const PeopleIcon = getTopicflowIcon("users");
const TeamIcon = getTopicflowIcon("team");
const StarIcon = getTopicflowIcon("star");
const ActivityIcon = getTopicflowIcon("activity");
const CreditCardIcon = getTopicflowIcon("creditCard");
const DataflowIcon = getTopicflowIcon("dataflow");
const SwitchHorizontalIcon = getTopicflowIcon("switchHorizontal");
const UsersCheckIcon = getTopicflowIcon("usersCheck");
const PreferencesIcon = getTopicflowIcon("adjustments");
const OrgChartIcon = getTopicflowIcon("organization");
const TemplatesIcon = getTopicflowIcon("program");

type SettingsNavItem = {
  label: string;
  to: string;
  icon: ReturnType<typeof getTopicflowIcon>;
};

const SidebarSettingsExpanded = () => {
  const leftSidePanel = useReactiveVar(leftSidePanelVar);
  const settingsPanelOpen = leftSidePanel === "settings";
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const label = useLabel();

  if (!settingsPanelOpen) {
    return null;
  }

  const nonPersonalOrganization = currentUser.organizations.edges.find(
    (edge) => edge?.node && !edge.node.personal,
  )?.node;
  const organizationWithSettingsAccess = currentUser.organizations.edges.find(
    (edge) =>
      (edge?.node || edge?.node?.userMembership) &&
      !edge.node.personal &&
      [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
        edge?.node?.userMembership?.role ?? "",
      ),
  );

  const membershipRole =
    organizationWithSettingsAccess?.node?.userMembership?.role ?? null;
  const isItAdmin = membershipRole === role.itAdmin;

  const accountItems: SettingsNavItem[] = compact([
    {
      label: "Account",
      to: "/account/profile",
      icon: UserIcon,
    },
    {
      label: "Preferences",
      to: "/account/preferences",
      icon: PreferencesIcon,
    },
    {
      label: "Meetings",
      to: "/account/meetings",
      icon: MeetingIcon,
    },
    {
      label: "Notifications",
      to: "/account/email",
      icon: NotificationIcon,
    },
    {
      label: "Integrations",
      to: "/account/integrations",
      icon: SwitchHorizontalIcon,
    },
    {
      label: "Export",
      to: "/account/export",
      icon: ExportIcon,
    },
    {
      label: "Directory Sync",
      to: "/account/directorysync",
      icon: UsersCheckIcon,
    },
  ]);

  const settingsOrgId = organizationWithSettingsAccess?.node?.id;
  const integrationsOrgId = settingsOrgId ?? nonPersonalOrganization?.id;

  const workspaceItems: SettingsNavItem[] = compact([
    currentOrganization.featureFlags.meetings && {
      label: "Meeting Templates",
      to: "/templates",
      icon: TemplatesIcon,
    },
    {
      label: "Org Chart",
      to: "/org-chart",
      icon: OrgChartIcon,
    },
    !isItAdmin &&
      settingsOrgId && {
        label: "People",
        to: `/settings/organization/${settingsOrgId}/members`,
        icon: PeopleIcon,
      },
    !isItAdmin &&
      settingsOrgId && {
        label: label("team", { capitalize: true, pluralize: true }),
        to: `/settings/organization/${settingsOrgId}/teams`,
        icon: TeamIcon,
      },
  ]);

  const orgItems: SettingsNavItem[] = integrationsOrgId
    ? compact([
        !isItAdmin &&
          settingsOrgId && {
            label: "Settings",
            to: `/settings/organization/${settingsOrgId}/settings`,
            icon: AdjustmentsIcon,
          },
        !isItAdmin &&
          settingsOrgId && {
            label: "AI",
            to: "/account/ai/workspace",
            icon: AiIcon,
          },
        !isItAdmin &&
          settingsOrgId && {
            label: "Core Values",
            to: `/settings/organization/${settingsOrgId}/core-values`,
            icon: StarIcon,
          },
        !isItAdmin &&
          settingsOrgId && {
            label: `Roles and ${label("competency", {
              capitalize: true,
              pluralize: true,
            })}`,
            to: `/settings/organization/${settingsOrgId}/competencies`,
            icon: DataflowIcon,
          },
        !isItAdmin &&
          settingsOrgId && {
            label: "Talent Indicators",
            to: `/settings/organization/${settingsOrgId}/talent-indicators`,
            icon: ActivityIcon,
          },
        !isItAdmin &&
          settingsOrgId && {
            label: "Billing",
            to: `/settings/organization/${settingsOrgId}/billing`,
            icon: CreditCardIcon,
          },
        settingsOrgId && {
          label: "HRIS Sync",
          to: `/settings/organization/${settingsOrgId}/directory-sync`,
          icon: UsersCheckIcon,
        },
        {
          label: "Integrations",
          to: `/settings/organization/${integrationsOrgId}/integrations`,
          icon: SwitchHorizontalIcon,
        },
        settingsOrgId && {
          label: "Notifications",
          to: `/settings/organization/${settingsOrgId}/notifications`,
          icon: NotificationIcon,
        },
      ])
    : [];

  const isActive = (to: string) =>
    window.location.href.startsWith(`${window.location.origin}${to}`);

  const renderSection = (title: string, items: SettingsNavItem[]) => (
    <div>
      <div className="px-5 pb-2">
        <div className="text-gray-500 text-xs">{title}</div>
      </div>
      <div className="flex flex-col gap-0.5 px-3">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => mobileSidebarOpenVar(false)}
            className={classNames(
              "flex items-center gap-2 px-2 py-1 rounded hover:bg-nav-item-active text-sm",
              isActive(item.to)
                ? "bg-nav-item-active text-gray-800"
                : "text-gray-800",
            )}
          >
            <item.icon className="shrink-0 h-4 w-4 text-gray-500" />
            <span className="font-normal truncate">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-sidenav-back flex-1 flex flex-col overflow-hidden border-gray-200">
      <div className="flex-1 overflow-y-auto scrollbar-hidden flex flex-col gap-4 pt-4">
        {renderSection("My Account Settings", accountItems)}
        {workspaceItems.length > 0 &&
          renderSection("Team Settings", workspaceItems)}
        {orgItems.length > 0 &&
          renderSection(
            label("organization", { capitalize: true }) + " Settings",
            orgItems,
          )}
      </div>
    </div>
  );
};

export default SidebarSettingsExpanded;
